import { useConvexNotifications } from "../../../services/convexService";

import { Badge } from "../../../components/ui/badge";
import { CheckCircle, Bell, TrendingUp, AlertCircle, XCircle } from "lucide-react";
import { Id } from "../../../../backend/_generated/dataModel";
import { Button } from "../../../components/ui/button";
import { Link } from "react-router-dom";

interface ProjectHolderNotificationsPreviewProps {
  userId: Id<"users">;
}

const ProjectHolderNotificationsPreview = ({ userId }: ProjectHolderNotificationsPreviewProps) => {
  const { getAllNotifications } = useConvexNotifications();

  // Get all notifications for the user (limit to 5 for preview)
  const allNotifications = getAllNotifications(userId);
  const previewNotifications = allNotifications ? allNotifications.slice(0, 5) : [];

  const formatDate = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-primary mt-1" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-500 mt-1" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-destructive mt-1" />;
      default:
        return <Bell className="w-5 h-5 text-muted-foreground mt-1" />;
    }
  };

  return (
    <div className="space-y-4">
      {previewNotifications.length > 0 ? (
        <>
          {previewNotifications.map((notification) => (
            <div
              key={notification._id}
              className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg"
            >
              {getNotificationIcon(notification.type)}
              <div className="flex-1">
                <p className="font-medium text-foreground">{notification.title}</p>
                <p className="text-sm text-muted-foreground">{notification.message}</p>
                <p className="text-xs text-muted-foreground mt-1">{formatDate(notification.createdAt)}</p>
              </div>
              <Badge variant={
                notification.type === 'success' ? 'default' :
                  notification.type === 'warning' ? 'destructive' :
                    notification.type === 'error' ? 'destructive' :
                      'secondary'
              }>
                {notification.type === 'success' && 'تم'}
                {notification.type === 'info' && 'معلومة'}
                {notification.type === 'warning' && 'تحذير'}
                {notification.type === 'error' && 'خطأ'}
              </Badge>
            </div>
          ))}
          <div className="mt-6">
            <Button variant="outline" className="w-full" asChild>
              <Link to="/notifications/project-holder">
                عرض كل الإشعارات
              </Link>
            </Button>
          </div>
        </>
      ) : (
        <div className="text-center py-8">
          <Bell className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">لا توجد إشعارات</h3>
          <p className="text-muted-foreground">سيظهر هنا الإشعارات المتعلقة بمشاريعك</p>
        </div>
      )}
    </div>
  );
};

export default ProjectHolderNotificationsPreview;